import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import sliderService, {
  LoadedSlider,
  PublicSlide,
  autoSlideMs,
  buildSlideImageUrl,
  isExternalLink,
  parseAspectRatio,
  toInternalPath,
} from '../../services/sliderService';

const ACCENT = '#3fced1';

export interface DynamicSliderProps {
  /** Location aus dem Slider-CMS (gleiche Location = gleiche Stelle in Web und App) */
  location: string;
  className?: string;
  /** Fallback-Seitenverhältnis, wenn der Slide keins hat */
  fallbackRatio?: string;
  /** Meldet die Zahl nutzbarer Slides (0 = Slider rendert nichts) – für Layout-Anpassungen im Parent */
  onSlidesChange?: (count: number) => void;
}

/**
 * Erster Web-Renderer des Slider-CMS (2026-09-15). Lädt Slider + aktive Slides einer
 * Location, rendert NICHTS solange geladen wird oder nichts da ist (kein Layout-Sprung
 * für leere Werbeflächen). Manual-Slides: Bild im Slide-Seitenverhältnis, optional
 * Titel/Untertitel/Button-Overlay, ganzer Slide verlinkt. Sponsor-Slides: Logos auf Weiß.
 * Auto-Slide nach autoSlideDuration (Default 5 s), Pause bei Hover, Pfeile + Punkte ab 2 Slides.
 */
const DynamicSlider: React.FC<DynamicSliderProps> = ({ location, className = '', fallbackRatio = '16:9', onSlidesChange }) => {
  const [data, setData] = useState<LoadedSlider | null>(null);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const notified = useRef<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    sliderService.loadByLocation(location).then((loaded) => {
      if (cancelled) return;
      setData(loaded);
      setIndex(0);
      const count = loaded?.slides.length ?? 0;
      if (notified.current !== count) {
        notified.current = count;
        onSlidesChange?.(count);
      }
    });
    return () => { cancelled = true; };
    // onSlidesChange bewusst nicht in den Deps (Parent-Callbacks sind oft nicht stabil)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  const count = data?.slides.length ?? 0;
  const go = useCallback((next: number) => {
    if (count === 0) return;
    setIndex(((next % count) + count) % count);
  }, [count]);

  useEffect(() => {
    if (count < 2 || paused) return;
    const t = window.setInterval(() => go(index + 1), autoSlideMs(data?.slider.autoSlideDuration));
    return () => window.clearInterval(t);
  }, [count, paused, index, go, data?.slider.autoSlideDuration]);

  if (!data || count === 0) return null;

  const current = data.slides[Math.min(index, count - 1)];
  const ratio = parseAspectRatio(current.aspectRatio ?? fallbackRatio, parseAspectRatio(fallbackRatio));

  return (
    <div
      className={`relative overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-900 ${className}`}
      style={{ aspectRatio: String(ratio) }}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      role="region"
      aria-roledescription="Slider"
      aria-label={data.slider.name}
      data-testid="dynamic-slider"
    >
      {data.slides.map((slide, i) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-500 ${i === index ? 'opacity-100' : 'pointer-events-none opacity-0'}`}
          aria-hidden={i !== index}
        >
          <SlideView slide={slide} sponsors={data.sponsors} />
        </div>
      ))}

      {count > 1 && (
        <>
          <button type="button" aria-label="Vorheriger Slide" onClick={() => go(index - 1)}
            className="absolute left-2 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-black/50 text-white hover:bg-black/70">
            <ChevronLeft size={18} />
          </button>
          <button type="button" aria-label="Nächster Slide" onClick={() => go(index + 1)}
            className="absolute right-2 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full bg-black/50 text-white hover:bg-black/70">
            <ChevronRight size={18} />
          </button>
          <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-1.5">
            {data.slides.map((s, i) => (
              <button key={s.id} type="button" aria-label={`Slide ${i + 1}`} onClick={() => go(i)}
                className={`h-1.5 rounded-full transition-all ${i === index ? 'w-4' : 'w-1.5 bg-white/50'}`}
                style={i === index ? { backgroundColor: ACCENT } : undefined} />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const LinkWrap: React.FC<{ href?: string | null; label: string; children: React.ReactNode }> = ({ href, label, children }) => {
  const url = (href ?? '').trim();
  if (!url) return <div className="block h-full w-full">{children}</div>;
  if (isExternalLink(url)) {
    return <a href={url} target="_blank" rel="noopener noreferrer sponsored" aria-label={label} className="block h-full w-full">{children}</a>;
  }
  return <Link to={toInternalPath(url)} aria-label={label} className="block h-full w-full">{children}</Link>;
};

const SlideView: React.FC<{ slide: PublicSlide; sponsors: LoadedSlider['sponsors'] }> = ({ slide, sponsors }) => {
  if (slide.type === 'sponsor') {
    const ids = new Set(slide.sponsorIds ?? []);
    const list = sponsors.filter((s) => ids.has(s.id) && s.active !== false);
    return (
      <div className="flex h-full w-full items-center justify-center bg-white p-4">
        <div className={`grid h-full w-full gap-4 ${list.length > 1 ? 'grid-cols-2' : 'grid-cols-1'}`}>
          {list.map((s) => {
            const img = buildSlideImageUrl(s.image);
            const inner = img ? <img src={img} alt={s.name ?? 'Sponsor'} className="h-full w-full object-contain" loading="lazy" />
              : <span className="text-sm font-bold text-zinc-800">{s.name}</span>;
            return (
              <div key={s.id} className="flex items-center justify-center">
                {s.url ? <a href={s.url} target="_blank" rel="noopener noreferrer sponsored" className="flex h-full w-full items-center justify-center">{inner}</a> : inner}
              </div>
            );
          })}
        </div>
      </div>
    );
  }
  const img = buildSlideImageUrl(slide.image);
  const hasText = Boolean(slide.title || slide.subtitle || slide.buttonText);
  return (
    <LinkWrap href={slide.linkUrl} label={slide.title || 'Slide öffnen'}>
      {img && <img src={img} alt={slide.title || ''} className="h-full w-full object-cover" loading="lazy" />}
      {hasText && (
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-4 pt-10">
          {slide.title && <p className="text-base font-extrabold leading-tight text-white">{slide.title}</p>}
          {slide.subtitle && <p className="mt-0.5 text-sm text-gray-200">{slide.subtitle}</p>}
          {slide.buttonText && (
            <span className="mt-2 inline-flex rounded-lg px-3 py-1.5 text-xs font-bold text-black" style={{ backgroundColor: ACCENT }}>{slide.buttonText}</span>
          )}
        </div>
      )}
    </LinkWrap>
  );
};

export default DynamicSlider;
